<template>
  <div class="gg-sites">
    <!-- Section header -->
    <div class="gg-sites__header">
      <h2 class="gg-sites__heading">Sites</h2>
      <button type="button" class="gg-outline-btn" @click="openSiteForm(null)">
        <v-icon class="gg-outline-btn__icon">mdi-plus</v-icon>
        <span class="gg-outline-btn__label">Novo site</span>
      </button>
    </div>

    <v-alert v-if="error" type="error" dense text class="mb-3">{{ error }}</v-alert>

    <div class="gg-sites__layout">
      <!-- Master: site rail -->
      <ul class="gg-sites__rail">
        <li v-for="site in sites" :key="site.sigla" class="gg-sites__rail-item">
          <button
            type="button"
            class="gg-site-item"
            :class="{ 'gg-site-item--active': site.sigla === selectedSigla }"
            :aria-current="site.sigla === selectedSigla ? 'true' : 'false'"
            @click="selectedSigla = site.sigla"
          >
            <span class="gg-site-item__name">{{ siteLabel(site) }}</span>
            <span class="gg-status">
              <span class="gg-status__dot" :class="`gg-status__dot--${site.active ? 'success' : 'muted'}`" />
              <span class="gg-status__label" :class="`gg-status__label--${site.active ? 'success' : 'muted'}`">
                {{ site.active ? 'ativo' : 'inativo' }}
              </span>
            </span>
          </button>
        </li>
        <li v-if="loading && !sites.length" class="gg-sites__rail-empty">Carregando…</li>
        <li v-else-if="!sites.length" class="gg-sites__rail-empty">Nenhum site cadastrado</li>
      </ul>

      <!-- Detail: selected site -->
      <div v-if="selectedSite" class="gg-sites__detail">
        <div class="gg-site-card">
          <!-- Identity row -->
          <div class="gg-site-card__header">
            <div class="gg-detail">
              <span class="gg-detail__label">Sigla</span>
              <span class="gg-detail__value">{{ selectedSite.sigla }}</span>
            </div>
            <div class="gg-detail gg-detail--name">
              <span class="gg-detail__label">Nome</span>
              <span class="gg-detail__value" :title="selectedSite.nome">{{ selectedSite.nome }}</span>
            </div>
            <div class="gg-detail gg-detail--url">
              <span class="gg-detail__label">Servidor MSE</span>
              <span v-if="mseBaseUrl" class="gg-url">
                <span class="gg-url__chip gg-url__chip--header" :title="mseBaseUrl">{{ mseBaseUrl }}</span>
                <button type="button" class="gg-copy-btn" aria-label="Copiar servidor MSE" @click="copy(mseBaseUrl)">
                  <v-icon class="gg-copy-btn__icon">mdi-content-copy</v-icon>
                </button>
                <!-- The MSE is probed like the other endpoints now, so it reads the
                     same way they do: dot + word. See the note on .gg-status--mse for
                     why it cannot sit on the rows' status column. -->
                <span class="gg-status gg-status--mse">
                  <span class="gg-status__dot" :class="`gg-status__dot--${mseStatus.tone}`" />
                  <span
                    class="gg-status__label"
                    :class="`gg-status__label--${mseStatus.tone}`"
                    :title="mseStatus.hint"
                  >
                    {{ mseStatus.label }}
                  </span>
                </span>
              </span>
              <span v-else class="gg-detail__value gg-detail__value--muted">—</span>
            </div>
            <div class="gg-detail gg-detail--status">
              <span class="gg-detail__label">Status</span>
              <span class="gg-status">
                <span class="gg-status__dot" :class="`gg-status__dot--${selectedSite.active ? 'success' : 'muted'}`" />
                <span class="gg-status__label" :class="`gg-status__label--${selectedSite.active ? 'success' : 'muted'}`">
                  {{ selectedSite.active ? 'ativo' : 'inativo' }}
                </span>
              </span>
            </div>
            <div class="gg-detail gg-detail--acoes">
              <span class="gg-detail__label">Ações</span>
              <div class="gg-icon-btn-group">
                <button type="button" class="gg-icon-btn" aria-label="Editar site" @click="openSiteForm(selectedSite)">
                  <v-icon class="gg-icon-btn__icon">mdi-pencil-outline</v-icon>
                </button>
                <button type="button" class="gg-icon-btn" aria-label="Excluir site" @click="confirmDeleteSite(selectedSite)">
                  <v-icon class="gg-icon-btn__icon">mdi-trash-can-outline</v-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- Endpoint groups: PDS / Graphic Hub / Preview, each principal + by -->
          <div v-for="group in endpointGroups" :key="group.tipo" class="gg-ep-group">
            <div class="gg-ep-group__title">{{ group.label }}</div>
            <div class="gg-ep-group__rows">
              <div v-for="ep in group.rows" :key="ep.papel" class="gg-ep-row">
                <span class="gg-ep-row__papel">{{ ep.papel === 'PRINCIPAL' ? 'Principal' : 'BY' }}</span>

                <span class="gg-ep-row__url">
                  <template v-if="ep.row">
                    <span class="gg-url__chip" :title="ep.row.baseUrl">{{ ep.row.baseUrl }}</span>
                    <button type="button" class="gg-copy-btn" aria-label="Copiar endereço" @click="copy(ep.row.baseUrl)">
                      <v-icon class="gg-copy-btn__icon">mdi-content-copy</v-icon>
                    </button>
                  </template>
                </span>

                <span class="gg-ep-row__status">
                  <span class="gg-status">
                    <span class="gg-status__dot" :class="`gg-status__dot--${ep.status.tone}`" />
                    <span class="gg-status__label" :class="`gg-status__label--${ep.status.tone}`" :title="ep.status.hint">
                      {{ ep.status.label }}
                    </span>
                  </span>
                </span>

                <span class="gg-ep-row__tag">
                  <span v-if="ep.inUse" class="gg-tag">Em uso</span>
                </span>

                <span class="gg-ep-row__acoes">
                  <button
                    type="button"
                    class="gg-icon-btn gg-icon-btn--plain"
                    :aria-label="ep.row ? 'Editar endpoint' : 'Adicionar endpoint'"
                    @click="openEndpointForm(selectedSite.sigla, ep, ep.row)"
                  >
                    <v-icon class="gg-icon-btn__icon gg-icon-btn__icon--sm">
                      {{ ep.row ? 'mdi-pencil-outline' : 'mdi-plus' }}
                    </v-icon>
                  </button>
                  <button
                    v-if="ep.row"
                    type="button"
                    class="gg-icon-btn gg-icon-btn--plain"
                    aria-label="Excluir endpoint"
                    @click="confirmDeleteEndpoint(selectedSite.sigla, ep)"
                  >
                    <v-icon class="gg-icon-btn__icon gg-icon-btn__icon--sm">mdi-trash-can-outline</v-icon>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create: full identity + MSE config, in the module's form drawer -->
    <GloboGraphicsSiteFormDrawer v-model="createOpen" @created="onSiteCreated" />

    <!-- Edit: only what a live site can change — sigla is its key, and the MSE
         naming/GUID are provisioning-managed. -->
    <v-dialog v-model="editForm.open" max-width="492" persistent content-class="gg-dialog">
      <div v-if="editForm.open" class="gg-dialog__card">
        <div class="gg-dialog__header">
          <h2 class="gg-dialog__title">Editar site “{{ editForm.sigla }}”</h2>
        </div>

        <div class="gg-dialog__body">
          <div class="gg-field-row">
            <div class="gg-field">
              <label class="gg-field__label" for="gg-edit-site-nome">Nome</label>
              <input
                id="gg-edit-site-nome"
                v-model="editForm.nome"
                type="text"
                class="gg-field__input"
                placeholder="Rio de Janeiro"
              />
            </div>
            <div class="gg-switch-field">
              <v-switch v-model="editForm.active" label="Ativo" hide-details dense class="gg-switch mt-0 pt-0" />
            </div>
          </div>

          <div class="gg-field">
            <label class="gg-field__label" for="gg-edit-site-url">URL</label>
            <input
              id="gg-edit-site-url"
              v-model="editForm.baseUrl"
              type="text"
              class="gg-field__input"
              placeholder="http://host:8580"
            />
          </div>

          <!-- Not in the design, which assumes a provisioned site. Kept because
               this is the only way to create the template show on a Trio-less MSE,
               so it appears only while the site still lacks one. -->
          <div v-if="!editForm.templateShowGuid" class="gg-field">
            <label class="gg-field__label">Template show guid</label>
            <button
              type="button"
              class="gg-inline-action"
              :disabled="provisioning || !editForm.baseUrl.trim()"
              @click="provisionShow"
            >
              <v-icon>mdi-plus-box-outline</v-icon>
              {{ provisioning ? 'Provisionando…' : 'Provisionar' }}
            </button>
          </div>
        </div>

        <div class="gg-dialog__footer">
          <button type="button" class="gg-btn gg-btn--text" @click="editForm.open = false">Cancelar</button>
          <button type="button" class="gg-btn gg-btn--primary" :disabled="!canSaveEdit" @click="saveSite">
            {{ saving ? 'Salvando…' : 'Salvar' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Endpoint URL — the only user-editable part; tipo/papel come from the row
         it was opened from, and the Graphic Hub credential is global. -->
    <v-dialog v-model="endpointForm.open" max-width="492" persistent content-class="gg-dialog">
      <div v-if="endpointForm.open" class="gg-dialog__card">
        <div class="gg-dialog__header">
          <h2 class="gg-dialog__title">{{ endpointDialogTitle }}</h2>
        </div>

        <div class="gg-dialog__body">
          <div class="gg-field">
            <label class="gg-field__label" for="gg-endpoint-url">
              URL<span v-if="!endpointForm.editing" class="gg-field__required" aria-hidden="true">*</span>
            </label>
            <input
              id="gg-endpoint-url"
              v-model="endpointForm.baseUrl"
              type="text"
              class="gg-field__input"
              :placeholder="endpointUrlExample"
            />
          </div>
        </div>

        <div class="gg-dialog__footer">
          <button type="button" class="gg-btn gg-btn--text" @click="endpointForm.open = false">Cancelar</button>
          <button
            type="button"
            class="gg-btn gg-btn--primary"
            :disabled="!canSaveEndpoint"
            @click="saveEndpoint"
          >
            {{ saving ? 'Salvando…' : 'Salvar' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Shared destructive confirmation (site / endpoint) -->
    <v-dialog v-model="deleteDialog.open" max-width="492" persistent content-class="gg-dialog">
      <div v-if="deleteDialog.open" class="gg-dialog__card">
        <div class="gg-dialog__header">
          <h2 class="gg-dialog__title">{{ deleteDialog.title }}</h2>
        </div>

        <div class="gg-dialog__body">
          <p class="gg-dialog__text">{{ deleteDialog.message }}</p>
        </div>

        <div class="gg-dialog__footer">
          <button type="button" class="gg-btn gg-btn--text" @click="deleteDialog.open = false">Cancelar</button>
          <button type="button" class="gg-btn gg-btn--critical" :disabled="saving" @click="runDelete">
            {{ saving ? 'Excluindo…' : deleteDialog.confirmLabel }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GloboGraphicsSiteFormDrawer from "./GloboGraphicsSiteFormDrawer.vue";

const TIPO_LABELS = { PDS: "PDS", GRAPHIC_HUB: "Graphic Hub", PREVIEW: "Preview" };
const TIPO_KEYS = { PDS: "pds", GRAPHIC_HUB: "graphicHub", PREVIEW: "preview" };
const PAPEL_LABELS = { PRINCIPAL: "Principal", BY: "BY" };

// Each service has its own scheme and port convention, and getting either wrong
// just yields an unreachable endpoint — so the example is per tipo, not generic.
const TIPO_URL_EXAMPLE = {
  PDS: "https://vizpilot-host:8177",
  GRAPHIC_HUB: "http://host:19398",
  PREVIEW: "http://host:19398",
};

// The API re-probes endpoint health every 30s and expires its per-pod site cache
// on the same beat, so this is the rate at which anything here can actually
// change — polling faster would only re-read an unchanged cache.
const SITES_POLL_MS = 30000;

export default {
  name: "GloboGraphicsSitesManager",
  components: { GloboGraphicsSiteFormDrawer },
  props: {
    // Panel stays mounted; (re)load when it becomes active.
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedSigla: null,
      saving: false,
      provisioning: false,
      pollTimer: null,
      createOpen: false,
      editForm: { open: false, sigla: "", nome: "", active: true, baseUrl: "", templateShowGuid: "" },
      endpointForm: { open: false, editing: false, sigla: "", id: null, tipo: "PDS", papel: "PRINCIPAL", baseUrl: "" },
      deleteDialog: { open: false, title: "", message: "", confirmLabel: "Excluir", action: null },
    };
  },
  computed: {
    ...mapGetters("sites_admin", {
      allSites: "getterAdminSites",
      loading: "getterAdminSitesLoading",
      error: "getterAdminSitesError",
    }),
    // The site the operator is actually working in (GG toolbar selection).
    ...mapGetters("sites", { activeSiteSigla: "getterSelectedSiteSigla" }),
    // The production's own site leads the rail — an admin arriving from a program
    // is nearly always here to look at that program's site, not whichever sigla
    // happens to sort first.
    sites() {
      const list = this.allSites || [];
      if (!this.activeSiteSigla) return list;
      const current = list.filter((s) => s.sigla === this.activeSiteSigla);
      if (!current.length) return list;
      return [...current, ...list.filter((s) => s.sigla !== this.activeSiteSigla)];
    },
    selectedSite() {
      if (!this.sites || !this.sites.length) return null;
      return this.sites.find((s) => s.sigla === this.selectedSigla) || null;
    },
    mseBaseUrl() {
      return this.selectedSite?.mse?.baseUrl || "";
    },
    // The MSE has no principal/by pair, so a failure is always critical — there is
    // nothing to fail over to.
    mseStatus() {
      return this.endpointStatus(this.selectedSite?.mse || null, null);
    },
    canSaveEdit() {
      return !!(this.editForm.nome.trim() && !this.saving);
    },
    canSaveEndpoint() {
      return !!(this.endpointForm.baseUrl.trim() && !this.saving);
    },
    endpointUrlExample() {
      return TIPO_URL_EXAMPLE[this.endpointForm.tipo] || "http://host:8080";
    },
    // "Configurar" when the slot is still empty, "Editar" once it holds a URL.
    endpointDialogTitle() {
      const { editing, tipo, papel, sigla } = this.endpointForm;
      const what = `${TIPO_LABELS[tipo]} - ${PAPEL_LABELS[papel]}`;
      return `${editing ? "Editar" : "Configurar"} endpoint “${what}” do site “${sigla}”`;
    },
    // PDS / Graphic Hub / Preview, each as a principal + by pair. "Em uso" marks
    // the endpoint actually serving traffic: principal when it is healthy,
    // otherwise the by it fails over to.
    endpointGroups() {
      const site = this.selectedSite;
      if (!site) return [];
      return ["PDS", "GRAPHIC_HUB", "PREVIEW"].map((tipo) => {
        const group = site[TIPO_KEYS[tipo]] || {};
        const principal = group.principal || null;
        const by = group.by || null;
        const servingKey = principal?.healthy === true ? "PRINCIPAL" : by?.healthy === true ? "BY" : null;
        const rows = [
          { papel: "PRINCIPAL", row: principal, partner: by },
          { papel: "BY", row: by, partner: principal },
        ].map((r) => ({
          ...r,
          tipo,
          tipoLabel: TIPO_LABELS[tipo],
          inUse: servingKey === r.papel,
          status: this.endpointStatus(r.row, r.partner),
        }));
        return { tipo, label: TIPO_LABELS[tipo], rows };
      });
    },
  },
  mounted() {
    if (this.active) this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    active(on) {
      if (on) this.startPolling();
      else this.stopPolling();
    },
    // Keep a valid selection as the list loads or the selected site disappears.
    sites: {
      handler(list) {
        if (!list || !list.length) {
          this.selectedSigla = null;
          return;
        }
        if (!list.some((s) => s.sigla === this.selectedSigla)) {
          this.selectedSigla = list[0].sigla;
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions("sites_admin", [
      "fetchSites",
      "updateSite", "deleteSite", "provisionTemplateShow",
      "createEndpoint", "updateEndpoint", "deleteEndpoint",
    ]),
    ...mapActions("pracas", ["getPracas"]),
    startPolling() {
      this.stopPolling();
      this.fetchSites().catch(() => {});
      // Praças are a near-static lookup, so they are loaded once rather than polled.
      this.getPracas();
      this.pollTimer = setInterval(() => {
        // A failed poll must not reject unhandled; the store already exposes the
        // message through getterAdminSitesError, which the panel renders.
        this.fetchSites().catch(() => {});
      }, SITES_POLL_MS);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    // A down endpoint is only critical when its partner cannot cover it — with a
    // healthy partner the pair is still serving, so it is a warning.
    endpointStatus(row, partner) {
      if (!row) return { label: "não configurado", tone: "muted", hint: "Nenhum endereço cadastrado" };
      if (row.healthy === true) {
        return {
          label: "disponível",
          tone: "success",
          hint: row.lastCheck ? `Acessível — ${new Date(row.lastCheck).toLocaleTimeString()}` : "Acessível",
        };
      }
      if (row.healthy === false) {
        return {
          label: "indisponível",
          tone: partner?.healthy === true ? "warning" : "critical",
          hint: row.lastError ? `Inacessível (${row.lastError})` : "Inacessível",
        };
      }
      return { label: "não verificado", tone: "muted", hint: "Ainda não verificado" };
    },

    async copy(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.$toast?.success?.("Endereço copiado");
      } catch (e) {
        this.$toast?.error?.("Não foi possível copiar o endereço");
      }
    },

    siteLabel(site) {
      return site?.nome ? `${site.sigla} - ${site.nome}` : site?.sigla || "";
    },

    // --- Site create / edit ---
    openSiteForm(item) {
      if (!item) {
        this.createOpen = true;
        return;
      }
      this.editForm = {
        open: true,
        sigla: item.sigla,
        nome: item.nome || "",
        active: item.active !== false,
        baseUrl: item.mse?.baseUrl || "",
        templateShowGuid: item.mse?.templateShowGuid || "",
      };
    },
    async provisionShow() {
      this.provisioning = true;
      try {
        const data = await this.provisionTemplateShow(this.editForm.sigla);
        // Reflect the created show back into the open dialog; fetchSites already
        // refreshed the list behind it.
        this.editForm.templateShowGuid = data?.templateShowGuid || this.editForm.templateShowGuid;
        this.$toast?.success?.("Template show provisionada");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao provisionar template show");
      } finally {
        this.provisioning = false;
      }
    },
    onSiteCreated(sigla) {
      this.selectedSigla = sigla;
    },
    async saveSite() {
      if (!this.canSaveEdit) return;
      this.saving = true;
      try {
        await this.updateSite({
          sigla: this.editForm.sigla,
          nome: this.editForm.nome.trim(),
          active: this.editForm.active,
          // Only the base URL is user-editable; the GUID is provisioning-managed and
          // the naming is standardized (read-only). Sending only baseUrl keeps a
          // partial patch from re-pinning a stale GUID / naming.
          mse: { baseUrl: this.editForm.baseUrl.trim() },
        });
        this.editForm.open = false;
        this.$toast?.success?.("Site salvo");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao salvar site");
      } finally {
        this.saving = false;
      }
    },
    confirmDeleteSite(item) {
      this.deleteDialog = {
        open: true,
        title: `Excluir site “${this.siteLabel(item)}”?`,
        message:
          "Esta ação é irreversível. Todos os endpoints e engines associados a esse site serão removidos permanentemente.",
        confirmLabel: "Excluir site",
        action: () => this.deleteSite(item.sigla),
      };
    },

    // --- Endpoint form ---
    openEndpointForm(sigla, ep, row) {
      this.endpointForm = {
        open: true,
        editing: !!row,
        sigla,
        id: row ? row.endpointId : null,
        tipo: ep.tipo,
        papel: ep.papel,
        baseUrl: row ? row.baseUrl : "",
      };
    },
    async saveEndpoint() {
      if (!this.canSaveEndpoint) return;
      this.saving = true;
      try {
        const { sigla, id, tipo, papel } = this.endpointForm;
        const baseUrl = this.endpointForm.baseUrl.trim();
        if (this.endpointForm.editing) {
          await this.updateEndpoint({ sigla, id, baseUrl });
        } else {
          await this.createEndpoint({ sigla, tipo, papel, baseUrl });
        }
        this.endpointForm.open = false;
        this.$toast?.success?.("Endpoint salvo");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao salvar endpoint");
      } finally {
        this.saving = false;
      }
    },
    confirmDeleteEndpoint(sigla, ep) {
      // Deleting an endpoint removes only its own row — engines hang off the site,
      // not the endpoint, so they are untouched. What is actually lost is the
      // failover partner for this tipo.
      const partnerNote =
        ep.partner
          ? ` O site passará a depender apenas do endpoint ${PAPEL_LABELS[ep.papel === "PRINCIPAL" ? "BY" : "PRINCIPAL"]} para ${ep.tipoLabel}.`
          : ` ${ep.tipoLabel} ficará sem nenhum endereço configurado.`;
      this.deleteDialog = {
        open: true,
        title: `Excluir endpoint “${ep.tipoLabel} - ${PAPEL_LABELS[ep.papel]}” do site “${sigla}”?`,
        message: `Esta ação é irreversível. O endereço cadastrado será removido permanentemente.${partnerNote}`,
        confirmLabel: "Excluir endpoint",
        action: () => this.deleteEndpoint({ sigla, id: ep.row.endpointId }),
      };
    },

    async runDelete() {
      if (!this.deleteDialog.action) return;
      this.saving = true;
      try {
        await this.deleteDialog.action();
        this.deleteDialog.open = false;
        this.$toast?.success?.("Excluído");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao excluir");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-sites {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-6);
}

/* Section header */
.gg-sites__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
}

.gg-sites__heading {
  margin: 0;
  color: var(--sc-gg-text-title);
  font-size: 20px;
  font-weight: var(--sc-weight-semi);
  line-height: 24px;
}

/* "Novo site" — the section header's only action */
.gg-outline-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  // Fixed height: the design clamps this to 40, and vertical padding on top of a
  // min-height pushed the whole section header to 46.
  height: 40px;
  padding: 0 6px;
  background: transparent;
  border: 1px solid var(--sc-gg-accent);
  border-radius: var(--sc-radius-md);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 2px;
  }
}

.gg-outline-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 20px !important;
}

.gg-outline-btn__label {
  padding: 0 var(--sc-space-1);
  color: var(--sc-gg-accent);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
}

/* Master / detail */
.gg-sites__layout {
  display: flex;
  align-items: stretch;
}

// Column geometry is the design's: a 304 rail, the rule inset 3px from it, then
// 32 of padding before the detail card — 304 / 307.5 / 343 across the 1353 content.
.gg-sites__rail {
  flex: 0 0 304px;
  width: 304px;
  margin: 0 3px 0 0;
  padding: 0;
  list-style: none;
}

// Rules are overlaid at the row boundaries rather than added as borders, so each
// row stays exactly 84 tall like the design's 0 / 84 / 168 stops.
.gg-sites__rail-item {
  position: relative;

  &::before,
  &:last-child::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--sc-gg-border-100);
  }

  &::before { top: 0; }
  &:last-child::after { bottom: 0; }
}

.gg-sites__rail-empty {
  padding: var(--sc-space-4);
  color: var(--sc-gg-text-tertiary);
  font-size: 14px;
}

.gg-site-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sc-space-2);
  width: 100%;
  padding: var(--sc-space-4);
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: -2px;
  }
}

.gg-site-item--active {
  background: rgba(255, 255, 255, 0.04);

  // Selection marker rides the rail's own divider, so it reads as the row
  // reaching into the detail panel.
  &::after {
    content: "";
    position: absolute;
    top: var(--sc-space-2);
    right: -1px;
    bottom: var(--sc-space-2);
    width: 4px;
    border-radius: var(--sc-radius-sm);
    background: var(--sc-gg-accent);
  }
}

.gg-site-item__name {
  color: var(--sc-gg-text-title);
  font-size: 16px;
  // 20, not the 24 of Body/MD: the row is 84 = 16 + 20 + 8 + 24 + 16.
  line-height: 20px;
  letter-spacing: -0.08px;
}

/* Status dot + label */
.gg-status {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
}

.gg-status__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--sc-radius-md);
}

.gg-status__dot--success  { background: var(--sc-gg-success-strong); }
.gg-status__dot--warning  { background: var(--sc-gg-warning); }
.gg-status__dot--critical { background: var(--sc-gg-error); }
.gg-status__dot--muted    { background: var(--sc-gg-text-tertiary); }

.gg-status__label {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  white-space: nowrap;
}


.gg-status__label--success  { color: var(--sc-gg-success); }
.gg-status__label--warning  { color: var(--sc-gg-warning); }
.gg-status__label--critical { color: var(--sc-gg-critical); }
.gg-status__label--muted    { color: var(--sc-gg-text-tertiary); }

/* Detail panel */
.gg-sites__detail {
  flex: 1 1 auto;
  min-width: 0;
  // 35 = the 3px on the far side of the rule + the column's own 32 of padding,
  // which is what puts the detail card at 343 and keeps it exactly 1010 wide.
  padding-left: 35px;
  border-left: 1px solid var(--sc-gg-border-100);
}

.gg-site-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
}

.gg-site-card__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
  padding: var(--sc-space-4);
  background: var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md) var(--sc-radius-md) 0 0;
}

// Pinned to the endpoint rows' status column so every "disponível" on the card —
// MSE, PDS, Graphic Hub, Preview — sits on one vertical line. 588 = the rows' 589
// from the card's border box, less this header's own 1px border.
// Out of flow, so the header's own column stops are untouched; the chip below is
// capped to keep the URL + copy button clear of this offset.
.gg-status--mse {
  position: absolute;
  left: 588px;
}

.gg-detail {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: var(--sc-space-4);
  min-width: 0;
}

// Every column is content-sized and space-between spreads the slack evenly — the
// design's columns sit at equal ~80px gaps, so a flexible Nome would eat them all.
// Nome is capped because it is the one free-text field here: left unbounded, a long
// site name pushes the whole header right until "ativo" collides with the pinned
// MSE status. Ellipsis keeps the full value in the title attribute.
.gg-detail--name   { max-width: 120px; }
// Reserves the room the pinned MSE status occupies (589 → ~677) so it stays inside
// the Servidor MSE column it describes. Widening this column also pulls it LEFT
// (space-between has less slack to hand out), which is what opens the gap between
// the URL's copy button and the pinned "disponível" — at 420 they were ~27px apart.
.gg-detail--url    { flex: 0 1 auto; min-width: 460px; }
// Pinned to the endpoint rows' "Em uso" column (799), like the MSE status is pinned
// to their status column — so the header's two values sit over the two row columns
// they correspond to. Content-width rather than the design's 126 so the box cannot
// reach under Ações and swallow clicks on the edit button.
.gg-detail--status {
  position: absolute;
  left: 798px;
}

.gg-detail__label {
  color: var(--sc-gg-text-secondary);
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.5px;
}

.gg-detail__value {
  overflow: hidden;
  color: var(--sc-gg-text-title);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gg-detail__value--muted { color: var(--sc-gg-text-tertiary); }

/* URL chip + copy */
.gg-url {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
  min-width: 0;
}

.gg-url__chip {
  overflow: hidden;
  max-width: 236px;
  padding: var(--sc-space-1) var(--sc-space-2);
  background: var(--sc-gg-bg);
  border-radius: var(--sc-radius-sm);
  color: var(--sc-gg-text-title);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

// Tighter than the endpoint rows' 236: the chip and its copy button have to finish
// before the status pinned at 589, and a long hostname would otherwise run under it.
// 190 still shows a full `http://10.0.0.0:8580`-style address without truncating.
.gg-url__chip--header {
  max-width: 190px;
  background: var(--sc-gg-surface-2);
}

.gg-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: var(--sc-space-1);
  background: transparent;
  border: none;
  border-radius: var(--sc-radius-pill);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 1px;
  }
}

.gg-copy-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 16px !important;
}

/* Icon buttons */
.gg-icon-btn-group {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-2);
  height: 36px;
}

.gg-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 6px;
  background: transparent;
  border: 1px solid var(--sc-gg-accent);
  border-radius: var(--sc-radius-lg);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 2px;
  }
}

.gg-icon-btn--plain {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--sc-radius-pill);
}

.gg-icon-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 24px !important;
}

.gg-icon-btn__icon--sm { font-size: 20px !important; }

/* Endpoint groups */
// Overlaid rule again, so a group is exactly 140 = 16 + 20 + 16 + 72 + 16.
.gg-ep-group {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-4);
  padding: var(--sc-space-4) 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--sc-gg-border-100);
  }
}

.gg-ep-group__title {
  padding: 0 var(--sc-space-4);
  color: var(--sc-gg-text-title);
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.08px;
}

.gg-ep-group__rows {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-2);
}

.gg-ep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-2);
  padding: 0 var(--sc-space-4);
}

.gg-ep-row__papel {
  flex: 0 0 152px;
  color: var(--sc-gg-text-title);
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
}

.gg-ep-row__url {
  display: inline-flex;
  flex: 0 0 288px;
  align-items: center;
  gap: var(--sc-space-1);
  min-width: 0;
}

.gg-ep-row__status { flex: 0 0 144px; }
.gg-ep-row__tag    { flex: 0 0 56px; }

.gg-ep-row__acoes {
  display: inline-flex;
  flex: 0 0 72px;
  align-items: center;
  gap: var(--sc-space-2);
}

/* "Em uso" tag */
.gg-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  min-height: 20px;
  padding: 2px var(--sc-space-1);
  background: var(--sc-gg-info-fill);
  border-radius: var(--sc-radius-sm);
  color: var(--sc-gg-accent);
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
</style>
